import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Users, Plus } from "lucide-react";
import TeamRow from "./TeamRow";
import AddTeamModal from "../AddTeamModal";
import Button from "components/common/Button";
import betaAPI from "v2/api";
import showToast from "utils/toast";

const TeamsSection = ({
  teams,
  workspaceName,
  workspaceId,
  onTeamCreated,
  className = "",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);

  const handleCreateTeam = async (teamData) => {
    try {
      await betaAPI.createTeam({ workspaceId, body: teamData });
      showToast.success(t("workspace.overview.teams.createSuccess"));
      setShowAddModal(false);
      onTeamCreated?.();
    } catch (err) {
      console.error("Failed to create team:", err);
      showToast.error(t("workspace.overview.teams.createFailed"));
    }
  };

  return (
    <div
      className={`flex flex-col rounded-xl border border-[#F3F4F6] bg-white ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
        <h2 className="text-sm font-semibold text-[#111827]">
          {t("workspace.overview.teams.title")}
        </h2>
        <Button onClick={() => setShowAddModal(true)} size="sm">
          {t("workspace.overview.teams.addTeam")}
        </Button>
      </div>

      <AddTeamModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCreate={handleCreateTeam}
        workspaceName={workspaceName}
      />

      {/* Team list */}
      <div className="flex-1">
        {teams.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-12">
            {/* Icon with decorative background */}
            <div className="relative mb-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#FEF3E7] to-[#FEE4CA]">
                <Users size={28} className="text-[#E07A3A]" />
              </div>
              <div className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-lg border border-[#F3F4F6] bg-white shadow-sm">
                <Plus size={12} className="text-[#9CA3AF]" />
              </div>
            </div>

            {/* Text content */}
            <h3 className="mb-1 text-sm font-semibold text-[#111827]">
              {t("workspace.overview.teams.emptyTitle")}
            </h3>
            <p className="mb-4 max-w-[200px] text-center text-xs leading-relaxed text-[#6B7280]">
              {t("workspace.overview.teams.emptyDescription")}
            </p>

            {/* CTA Button */}
            <button
              onClick={() => setShowAddModal(true)}
              className="rounded-lg bg-[#FEF3E7] px-4 py-2 text-xs font-medium text-[#E07A3A] transition duration-150 hover:bg-[#FEE4CA]"
            >
              {t("workspace.overview.teams.addTeam")}
            </button>
          </div>
        ) : (
          teams.map((team, index) => (
            <TeamRow
              key={team.id}
              team={team}
              isLast={index === teams.length - 1}
              onClick={() => navigate(`/v2/${workspaceId}/team/${team.id}`)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default TeamsSection;
